'use client'

import assistantMarkdown from '@/components/Chat/assistantMarkdown.module.css'
import AttachFileItem from '@/components/Chat/AttachFileItem'
import { CompactBreakpointMessage } from '@/components/ChatV2/CompactBreakpointMessage'
import { GoogleDriveFilePicker } from '@/components/ChatV2/GoogleDriveFilePicker'
import { IntegrationSignInButton } from '@/components/ChatV2/IntegrationSignInButton'
import { PipedreamSignInButton } from '@/components/ChatV2/PipedreamSignInButton'
import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import { selectInteractions } from '@/lib/features/copilotv2/ChatSlice'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { useAppSelector } from '@/lib/hooks'
import {
  assertNever,
  Attachment,
  ChatItemV2,
  ChatItemV2Assistant,
  ChatItemV2ToolProgress,
  ChatItemV2User,
  isToolProgressMessage,
  ProgressStep,
  ToolProgressMessage,
} from '@/lib/type'
import { transformHeadlineForDisplay } from '@/lib/utils'
import { motion } from 'framer-motion'
import { RotateCcw } from 'lucide-react'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'react-toastify'
import { CopilotCompactBreakpoint } from 'sprites-types/api/v2/copilot'
import AssistantTypewriterMessage from './AssistantTypewriterMessage'
import ProgressMessageItem from './ProgressMessageItem'

const fadeAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.3 },
}

const isImageExtension = (ext: string): boolean => {
  const imageExts = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'bmp',
    'svg',
    'tiff',
    'tif',
    'avif',
  ])
  return imageExts.has(ext.toLowerCase())
}

// Type guard to check if a message is a compact breakpoint
const isCompactBreakpoint = (message: ChatItemV2): message is CopilotCompactBreakpoint => {
  return (
    message.role === 'system' && 'type' in message && (message as any).type === 'compact_breakpoint'
  )
}

// User message component - handles context display on hover
const UserMessage = memo(function UserMessage({
  chatMessage,
  shouldAnimate = true,
}: {
  chatMessage: ChatItemV2User
  shouldAnimate?: boolean
}) {
  // Always call hooks at the top
  const { rollbackToMessage } = useCopilotContext()
  const interactions = useAppSelector(selectInteractions) // Get from Redux instead of context
  const [showRollbackButton, setShowRollbackButton] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { status } = useCopilotContext()

  // Check if this message can be rolled back to
  const canRollback = useMemo(() => {
    // Quick early returns to avoid expensive calculations
    if (chatMessage.role !== 'user' || !interactions || interactions.length === 0) {
      return false
    }

    // Find which interaction this message belongs to (optimized)
    for (let i = 0; i < interactions.length; i++) {
      const interaction = interactions[i]
      const firstUserMessage = interaction.messages.find((msg) => msg.role === 'user')

      if (firstUserMessage?.id === chatMessage.id) {
        return true
      }
    }

    return false
  }, [interactions, chatMessage.id, chatMessage.role])

  // Check if rollback should be disabled due to active SSE connection
  const isConnectionActive = status === 'streaming'

  // Update tooltip position when it's shown
  useEffect(() => {
    if (showTooltip && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.left - 8, // Position to the left of the button
      })
    }
  }, [showTooltip])

  const handleRollback = async () => {
    // Prevent rollback if connection is active
    if (isConnectionActive) {
      toast.warn('Cannot rollback while Copilot is actively responding')
      return
    }

    try {
      const rolledBack = await rollbackToMessage(chatMessage.id)
      if (rolledBack) {
        console.log('Rollback successful')
        toast.success('Conversation rolled back successfully')
      } else {
        toast.error('Unable to rollback to this message')
      }
    } catch (error) {
      console.error('Failed to rollback:', error)
      toast.error('Failed to rollback conversation')
    }
  }

  const renderMarkdownPreview = (content: string) => {
    // Only normalize Windows CRLF to standard newlines
    // Don't convert \\n to \n as remarkBreaks will handle real newlines properly
    const normalized = content.replace(/\r\n/g, '\n')

    return (
      <EnhancedMarkdown
        className={assistantMarkdown['wm-markdown']}
        style={{
          background: 'transparent',
          color: '#000',
        }}
      >
        {normalized}
      </EnhancedMarkdown>
    )
  }

  const bubbleStyle = {
    overflowWrap: 'break-word' as const,
    wordBreak: 'break-word' as const,
    fontFamily: 'Hanken Grotesk, sans-serif',
    width: 'fit-content',
    maxWidth: '100%',
  }

  // Check if this is a command message (starts with '/')
  const isCommand = chatMessage.content?.trim().startsWith('/')

  // Only show UserMessage for user messages with content or messages that contain an attachment
  if (
    chatMessage.role !== 'user' ||
    (!chatMessage.content &&
      (!chatMessage.context?.files || chatMessage.context.files.length === 0))
  ) {
    return null
  }

  const WrapperComponent = shouldAnimate ? motion.div : 'div'
  const animationProps = shouldAnimate ? fadeAnimation : {}

  return (
    <WrapperComponent {...animationProps}>
      <div className="flex w-full flex-row-reverse gap-x-1">
        <div className="flex grow flex-col gap-y-2 overflow-hidden">
          {chatMessage.context?.files && chatMessage.context.files.length > 0 && (
            <div className="flex flex-row-reverse flex-wrap gap-2">
              {chatMessage.context.files.map((att) => {
                const file = att as Attachment
                const isImage = isImageExtension(file.ext)
                if (isImage && file.url) {
                  return (
                    <div
                      key={`${file.filename}-${file.url}`}
                      className="overflow-hidden rounded-xl border border-neutral-200 bg-white p-1 shadow-sm"
                      style={{ maxWidth: 320 }}
                      title={file.filename}
                    >
                      <Image
                        src={file.url}
                        alt={file.filename}
                        width={320}
                        height={320}
                        className="max-h-64 w-full rounded-lg object-contain"
                      />
                    </div>
                  )
                }
                return (
                  <AttachFileItem
                    key={file.filename}
                    filename={file.filename}
                    isRemoveBtn={false}
                    isClickable={true}
                    onPreviewAction={() => {
                      window.open(file.url, '_blank')
                    }}
                  />
                )
              })}
            </div>
          )}
          <div
            className="mb-2 flex max-w-full flex-row items-start gap-3 self-end sm:max-w-md md:max-w-lg lg:max-w-2xl"
            onMouseEnter={() => {
              canRollback && setShowRollbackButton(true)
            }}
            onMouseLeave={() => {
              canRollback && setShowRollbackButton(false)
            }}
          >
            {/* Rollback Button or Spacer */}
            {canRollback ? (
              showRollbackButton ? (
                <>
                  <button
                    ref={buttonRef}
                    onClick={handleRollback}
                    onMouseEnter={() => {
                      if (!isConnectionActive) {
                        tooltipTimerRef.current = setTimeout(() => {
                          setShowTooltip(true)
                        }, 200)
                      }
                    }}
                    onMouseLeave={() => {
                      if (tooltipTimerRef.current) {
                        clearTimeout(tooltipTimerRef.current)
                        tooltipTimerRef.current = null
                      }
                      setShowTooltip(false)
                    }}
                    disabled={isConnectionActive}
                    className={`mt-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 shadow-sm transition-all duration-200 ${
                      isConnectionActive
                        ? 'cursor-not-allowed bg-gray-50 text-gray-400 opacity-50'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                  >
                    <RotateCcw size={14} />
                  </button>
                  {showTooltip &&
                    !isConnectionActive &&
                    typeof document !== 'undefined' &&
                    createPortal(
                      <div
                        className="font-hanken-grotesk pointer-events-none fixed z-[9999] -translate-x-full -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg"
                        style={{
                          top: `${tooltipPosition.top}px`,
                          left: `${tooltipPosition.left}px`,
                        }}
                      >
                        Rollback conversation to this message
                      </div>,
                      document.body
                    )}
                </>
              ) : (
                <div className="mt-2 h-8 w-8 flex-shrink-0" />
              )
            ) : null}

            {!chatMessage.wasEmptyContent && (
              <div
                className={`font-hanken-grotesk relative rounded-3xl px-4 py-3 shadow-sm ${
                  isCommand
                    ? 'border border-blue-200 bg-blue-50 text-blue-700'
                    : 'bg-[#ededed] text-black'
                }`}
                style={bubbleStyle}
              >
                {renderMarkdownPreview(chatMessage.content || '')}
              </div>
            )}
          </div>
        </div>
      </div>
    </WrapperComponent>
  )
})

// Regular assistant message component - no typewriter effect
const AssistantMessage = memo(function AssistantMessage({
  chatMessage,
  shouldAnimate = true,
  useTightSpacing = false,
}: {
  chatMessage: ChatItemV2Assistant
  shouldAnimate?: boolean
  useTightSpacing?: boolean
}) {
  if (
    !('role' in chatMessage) ||
    chatMessage.role !== 'assistant' ||
    typeof (chatMessage as any).content !== 'string'
  )
    return null
  const content = (chatMessage as any).content

  const renderMarkdownPreview = (content: string) => {
    // Only normalize Windows CRLF to standard newlines
    // Don't convert \\n to \n as remarkBreaks will handle real newlines properly
    const normalized = content.replace(/\r\n/g, '\n')

    return (
      <EnhancedMarkdown
        className={assistantMarkdown['wm-markdown']}
        style={{
          background: 'transparent',
        }}
      >
        {normalized}
      </EnhancedMarkdown>
    )
  }

  const WrapperComponent = shouldAnimate ? motion.div : 'div'
  const animationProps = shouldAnimate ? fadeAnimation : {}

  return (
    <WrapperComponent {...animationProps}>
      <div className="flex w-full gap-x-1">
        <div className="flex grow flex-col gap-y-2 overflow-hidden">
          <div
            className={`${useTightSpacing ? 'mb-1' : 'mb-2'} max-w-full self-start sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl`}
          >
            <div
              className="font-hanken-grotesk relative rounded-3xl px-4 py-3 text-[#2D1A47]"
              style={{
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                fontFamily: 'Hanken Grotesk, sans-serif',
                width: 'fit-content',
                maxWidth: '100%',
              }}
            >
              {renderMarkdownPreview(content)}
            </div>
          </div>
        </div>
      </div>
    </WrapperComponent>
  )
})

// Tool progress message component
const ToolProgressMessageItem = memo(function ToolProgressMessageItem({
  chatMessage,
  useTightSpacing = false,
}: {
  chatMessage: ChatItemV2ToolProgress
  useTightSpacing?: boolean
}) {
  const searchParams = useSearchParams()
  const isDebugMode = searchParams.get('debug') === 'true'

  const { headline, progressMessages, expanded, status, tool_call_id, id, tool } = chatMessage

  // executionId exists on workflow_execution_tool_status type, but failed workflows
  // might be sent as regular tool_call_status - check for the property directly
  const executionId = 'executionId' in chatMessage ? chatMessage.executionId : undefined
  const toolResultContent =
    'toolResultContent' in chatMessage ? chatMessage.toolResultContent : undefined

  // Look up the tool call inputs from the assistant message with matching tool_call_id.
  // Copilot tool inputs live in the assistant message's universal_tool_calls (or legacy tool_calls).
  const interactions = useAppSelector(selectInteractions)
  const toolCallInputs = useMemo<unknown>(() => {
    if (!tool_call_id) return undefined
    for (const interaction of interactions) {
      for (const msg of interaction.messages) {
        if (msg.role !== 'assistant') continue
        const assistantMsg = msg as ChatItemV2Assistant
        // Prefer universal_tool_calls (parsed arguments object)
        const universalMatch = assistantMsg.universal_tool_calls?.find(
          (tc) => tc.id === tool_call_id
        )
        if (universalMatch) return universalMatch.arguments
        // Fall back to legacy tool_calls (arguments is a JSON string)
        const legacyMatch = assistantMsg.tool_calls?.find((tc) => tc.id === tool_call_id)
        if (legacyMatch) {
          try {
            return JSON.parse(legacyMatch.function.arguments)
          } catch {
            return legacyMatch.function.arguments
          }
        }
      }
    }
    return undefined
  }, [tool_call_id, interactions])

  const mapStatus = (
    s: 'started' | 'completed' | 'failed'
  ): 'in-progress' | 'success' | 'error' => {
    if (s === 'started') return 'in-progress'
    if (s === 'completed') return 'success'
    if (s === 'failed') return 'error'
    return 'in-progress'
  }
  const stepStatus = mapStatus(status)
  // Filter out hidden messages and convert to ProgressStep format (unless debug mode is enabled)
  const visibleMessages: ToolProgressMessage[] = progressMessages.filter((msg) =>
    isDebugMode ? true : !msg.hidden
  )

  const steps: ProgressStep[] = visibleMessages.map((msg, index) => ({
    id: msg.id,
    label: msg.content,
    // Only the last step should inherit the tool's final status (actual error)
    // All other steps should be 'success' since they completed successfully
    status: index === visibleMessages.length - 1 ? stepStatus : 'success',
    subtitles: [msg.content],
    icon: msg.icon,
  }))
  if (steps.length === 0) {
    steps.push({
      id: '0',
      label: headline,
      status: stepStatus,
      subtitles: [headline],
    })
  }

  const messageId = tool_call_id || id || 'tool-progress'
  const displayHeadline = transformHeadlineForDisplay(headline, status)
  return (
    <div className={useTightSpacing ? 'my-0' : 'my-1'}>
      <ProgressMessageItem
        messageId={messageId}
        steps={steps}
        expanded={expanded}
        headline={displayHeadline}
        tool={tool}
        executionId={executionId}
        toolResultContent={toolResultContent}
        toolCallInputs={toolCallInputs}
      />
    </div>
  )
})

// Main streaming message item component
const ChatMessageItem = memo(function ChatMessageItem({
  chatMessage,
  showDividerAbove = false,
  shouldAnimate = true,
  onContentChange,
  useTightSpacing = false,
}: {
  chatMessage: ChatItemV2
  showDividerAbove?: boolean
  shouldAnimate?: boolean
  onContentChange?: () => void
  useTightSpacing?: boolean
}) {
  // Always call hooks at the top
  const { activeTypewriterMessageId, hasHydrated, sendMessage } = useCopilotContext()
  const [hasBeenTypewriter, setHasBeenTypewriter] = useState(false)

  // Only the message with activeTypewriterMessageId should animate
  const isActiveTypewriter = chatMessage.id === activeTypewriterMessageId

  // Track if this message has ever been a typewriter message
  useEffect(() => {
    if (isActiveTypewriter && !hasBeenTypewriter) {
      setHasBeenTypewriter(true)
    }
  }, [isActiveTypewriter, hasBeenTypewriter])

  // For assistant messages, use the typewriter effect
  if (chatMessage.role === 'assistant') {
    // Handle unified integration sign-in
    if (chatMessage.signInDetails) {
      const signInType = chatMessage.signInDetails.type

      switch (signInType) {
        case 'pipedream':
          // For now, we only support the first appSlug
          const appSlug = chatMessage.signInDetails.appSlugs[0]
          return <PipedreamSignInButton appSlug={appSlug} sendMessage={sendMessage} />

        case 'googleAds':
        case 'googleSearchConsole':
        case 'wordpress':
        case 'framer':
        case 'facebook':
        case 'webflow':
        case 'shopify':
          return <IntegrationSignInButton />

        default:
          assertNever(signInType)
      }
    } else if (chatMessage.googleDriveFilePickerInfo) {
      const filePickerInfo = chatMessage.googleDriveFilePickerInfo
      return <GoogleDriveFilePicker filePickerInfo={filePickerInfo} sendMessage={sendMessage} />
    }

    if (isActiveTypewriter) {
      return (
        <>
          {showDividerAbove && (
            <div className="max-w-[80%]">
              <div className="my-1 border-t border-gray-600" />
            </div>
          )}
          <AssistantTypewriterMessage
            chatMessage={chatMessage}
            shouldAnimate={shouldAnimate}
            onContentChange={onContentChange}
            useTightSpacing={useTightSpacing}
          />
        </>
      )
    } else if (hasBeenTypewriter || hasHydrated) {
      return (
        <>
          {showDividerAbove && (
            <div className="max-w-[80%]">
              <div className="my-1 border-t border-gray-600" />
            </div>
          )}
          <AssistantMessage
            chatMessage={chatMessage}
            shouldAnimate={shouldAnimate}
            useTightSpacing={useTightSpacing}
          />
        </>
      )
    } else {
      return null
    }
  }

  // For tool messages, show during hydration or after their turn
  if (chatMessage.role === 'tool') {
    if (isActiveTypewriter || hasBeenTypewriter || hasHydrated) {
      if (isToolProgressMessage(chatMessage)) {
        return (
          <ToolProgressMessageItem chatMessage={chatMessage} useTightSpacing={useTightSpacing} />
        )
      }
      return null
    } else {
      return null
    }
  }

  // For system messages, show during hydration or after their turn
  if (chatMessage.role === 'system') {
    // Check if this is a compact breakpoint message - these should always show immediately
    if (isCompactBreakpoint(chatMessage)) {
      return <CompactBreakpointMessage message={chatMessage} />
    }

    // For other system messages, only show during hydration or after their turn
    if (isActiveTypewriter || hasBeenTypewriter || hasHydrated) {
      // Handle regular system messages with 'message' field
      if ('message' in chatMessage && typeof chatMessage.message === 'string') {
        return (
          <div className="my-2 text-center text-base text-neutral-500">{chatMessage.message}</div>
        )
      }

      // Only show loading for non-compact-breakpoint messages that don't have content
      if (
        !isCompactBreakpoint(chatMessage) &&
        (!('message' in chatMessage) || !chatMessage.message)
      ) {
        return <LoadingMessage />
      }

      return null
    } else {
      return null
    }
  }

  // User message: always show immediately
  if (chatMessage.role === 'user') {
    return <UserMessage chatMessage={chatMessage} shouldAnimate={shouldAnimate} />
  }

  // Fallback
  return null
})

export default ChatMessageItem

// Simplified loading component for streaming
function LoadingMessage() {
  return (
    <div className="flex items-center justify-center py-4">
      <span className="text-neutral-400">Loading...</span>
    </div>
  )
}
